import { AttackPattern, PatternState } from '../AttackPattern.js';

let nextPauseToken = 1;

const WARN_WINDOW = 0.35; // frozen bullets flash white this long before the release

/**
 * Grand Pause (The Iron Cadence's signature) — the caesura. The conductor
 * cuts the whole orchestra off: EVERY bullet in the boss's pool freezes
 * mid-air, hanging exactly where it was, while the fermata holds… then the
 * baton drops and every one of them resumes AT ONCE, faster than it left.
 * The safe lane you were tracking is suddenly a photograph you're standing
 * inside of — the skill is using the silence to walk out of the picture
 * before the music restarts. Temporal Rewind sends bullets backward; the
 * Grand Pause is crueller: they finish exactly the flight you already saw
 * coming, at surge speed.
 *
 * Layered patterns' bullets get caught too — deliberately, same "the whole
 * fight obeys one clock" rule as the Chronophage's rewind. Captured
 * bullets are flagged `_grandPaused` so per-tick steerers (Staccato
 * Volley) leave them alone until the release hands them back.
 */
export class GrandPause extends AttackPattern {
  constructor({ minPhase = 1, config }) {
    super({ name: 'Grand Pause', telegraphDuration: 1.1, activeDuration: 3.2, minPhase });
    this.config = config;
  }

  onBegin(boss) {
    this._token = nextPauseToken++;
    this._held = [];
    this._elapsed = 0;
    this._released = false;
    this._holdDuration = this.config.holdDuration + boss.phase * 0.15;
  }

  onActiveStart(_boss, _player, pool) {
    // The cutoff: capture everything in flight exactly where it hangs.
    pool.forEachActive((bullet) => {
      if (bullet._grandPaused) return; // a layered second Pause never double-captures
      this._held.push({
        bullet,
        vx: bullet.vx,
        vy: bullet.vy,
        accel: bullet.accel,
        color: bullet.color,
      });
      bullet._grandPaused = this._token;
      bullet.vx = 0;
      bullet.vy = 0;
      bullet.speed = 0;
      bullet.accel = 0;
      // The hold must never silently expire a bullet the player is
      // counting on dodging AFTER the release.
      bullet.lifetime += this._holdDuration;
    });
  }

  onActiveTick(dt, boss, _player, _pool) {
    this._elapsed += dt;
    if (this._released) return;

    const warning = this._elapsed >= this._holdDuration - WARN_WINDOW;
    if (warning) {
      for (const entry of this._held) {
        if (entry.bullet.active && entry.bullet._grandPaused === this._token) {
          entry.bullet.color = '#ffffff';
        }
      }
    }

    if (this._elapsed >= this._holdDuration) {
      this._release(boss);
    }
  }

  _release(boss) {
    this._released = true;
    const surge = this.config.surgeMultiplier;
    for (const { bullet, vx, vy, accel, color } of this._held) {
      if (!bullet.active || bullet._grandPaused !== this._token) continue;
      bullet._grandPaused = null;
      bullet.vx = vx * surge;
      bullet.vy = vy * surge;
      bullet.speed = Math.hypot(bullet.vx, bullet.vy);
      bullet.accel = accel;
      bullet.color = color;
    }
    this._held = [];
    boss.eventBus?.emit('cadence:surge');
    // The conductor overextends on the big downbeat — the reward window.
    boss.openVulnerability(0.7, 1.5);
  }

  onEnd(boss) {
    // Defensive: if the pattern somehow ends mid-hold (activeDuration
    // shorter than the hold after a config change), never leave frozen
    // orphans behind.
    if (!this._released) this._release(boss);
  }

  getVisual(boss) {
    if (this.state === PatternState.TELEGRAPH) {
      return { kind: 'fermata', x: boss.x, y: boss.y, t: 1 - this._timer / this.telegraphDuration, holding: false, warn: false };
    }
    if (this.state === PatternState.ACTIVE && !this._released) {
      return {
        kind: 'fermata',
        x: boss.x,
        y: boss.y,
        t: 1,
        holding: true,
        warn: this._elapsed >= this._holdDuration - WARN_WINDOW,
      };
    }
    return null;
  }
}
